import { translateBackendError } from '../../utils/translateBackendError';

function getFriendlyError(errorType, errorMessage, { tChat, tAlerts } = {}) {
    // Tarefa 4 (specs/i18n_erros_sistema/plan.md): erros de upload de arquivo
    // (limite de 20MB, limite de tokens sem modelo, falha de download) agora
    // chegam como `{ error_code, error_params }` em vez de texto pronto - só
    // esses passam por translateBackendError; o resto (texto de exceção cru,
    // classificações antigas por substring) continua como está (FR-006).
    if (errorMessage && typeof errorMessage === 'object' && errorMessage.error_code) {
        return translateBackendError(errorMessage);
    }

    const errorStr = errorType || errorMessage || '';

    if (!errorStr) return tChat ? tChat('file_no_sent') : '';

    if (errorStr.includes("exceeds the supported page limit")) {
        return tChat ? tChat('file_pages_limit') : errorStr;
    }

    if (errorStr.includes("INVALID_ARGUMENT")) {
        return tChat ? tChat('file_bytes_limit') : errorStr;
    }

    if (errorStr.includes("token_limit")) {
        return tAlerts ? tAlerts('file_tokens_limit') : errorStr;
    }

    if (errorStr.includes("size_limit")) {
        // O backend já manda uma mensagem específica (arquivo, tamanho, limite) —
        // mostra ela em vez do texto genérico, que descartaria essa informação.
        return errorMessage || (tChat ? tChat('file_no_sent') : errorStr);
    }

    return tChat ? tChat('file_no_sent') : errorStr;
}

function buildUploadAlert(result, failed, { tChat, tAlerts }) {
    const alertMessages = [];
    let alertType = "info";
    let alertTitle = tAlerts('model_changed');

    if (result.model_changed) {
        alertMessages.push(tAlerts('model_changed_message'));
    }

    if (failed.length > 0) {
        const hasTokenError = failed.some(
            f => f.error_type === "token_limit"
        );

        alertTitle = hasTokenError
            ? tAlerts('file_tokens_title')
            : tChat('alertErrorTitle');

        alertType = "error";

        const errorMessages = failed
            .map(
                f =>
                    `${f.filename}: ${getFriendlyError(
                        f.error_type,
                        f.error,
                        { tChat, tAlerts }
                    )}`
            )
            .join('\n');

        alertMessages.push(errorMessages);
    }

    return {
        show: alertMessages.length > 0,
        title: alertTitle,
        type: alertType,
        message: alertMessages.join('\n\n')
    };
}

// Estado da tool no vocabulario do painel/indicador de atividade. O
// transporte usa running/ok/error; a UI usa in_progress/completed/error.
function statusDeAtividade(statusDoEvento) {
    if (statusDoEvento === 'running') return 'in_progress';
    if (statusDoEvento === 'error') return 'error';
    return 'completed';
}

// Instante em que algo comecou, a partir da duracao que o SERVIDOR entregou
// pronta (contrato de post_execution_progress). Nunca subtrai relogio de
// servidor de relogio de browser - os dois nao estao sincronizados.
function inicioPelaDuracao(duracaoMs, agora) {
    return agora - (duracaoMs || 0);
}

// Etapa que o indicador inline (ActivityStepInline) mostra para uma execucao
// reidratada. Dentro de skill o texto e' o mesmo do fluxo ao vivo ("Acionando
// skill X"); fora dela, vira uma etapa "tool" (mesmo formato que o modo skill
// usa por chamada) com o rotulo traduzido da propria tool, em vez de agrupar
// por tipo (writing/analyzing/preparing_file) - mesma decisao de 2026-09-09
// aplicada ao fluxo ao vivo (UnifiedChat.js) e ao historico recarregado
// (buildActivityFromExecutionEvents.js): agrupar escondia quantas vezes o
// agente rodou a mesma ferramenta.
function passoDeAtividade(progresso, rotuloDaTool) {
    if (progresso.skill_name) {
        return {
            type: 'skill',
            status: 'in_progress',
            data: { skill_name: progresso.skill_name },
        };
    }
    const eventos = progresso.execution_events || [];
    const ultimo = eventos[eventos.length - 1];
    if (!ultimo) return null;
    return {
        type: 'tool',
        status: 'in_progress',
        data: { tool_id: ultimo.id, tool_label: rotuloDaTool(ultimo) },
    };
}

// Timeline do painel "Atividade" para uma execucao reidratada, no mesmo
// formato que o fluxo ao vivo monta em UnifiedChat.js: um passo "skill" com a
// lista de tools dentro, ou uma linha "tool" por chamada quando nao ha skill.
function timelineDeAtividade(progresso, agora, rotuloDaTool) {
    const eventos = progresso.execution_events || [];
    if (eventos.length === 0) return [];

    const linhaDeTool = evento => ({
        tool_id: evento.id,
        tool_label: rotuloDaTool(evento),
        status: statusDeAtividade(evento.status),
        startedAt: inicioPelaDuracao(evento.duracao_ms, agora),
        durationMs: evento.status === 'running' ? undefined : (evento.ms ?? evento.duracao_ms),
    });

    if (progresso.skill_name) {
        return [{
            type: 'skill',
            status: 'in_progress',
            startedAt: inicioPelaDuracao(progresso.duracao_ms, agora),
            data: { skill_name: progresso.skill_name, tools: eventos.map(linhaDeTool) },
        }];
    }

    return eventos.map(evento => ({
        type: 'tool',
        status: statusDeAtividade(evento.status),
        data: { tool_id: evento.id, tool_label: rotuloDaTool(evento) },
        startedAt: inicioPelaDuracao(evento.duracao_ms, agora),
        durationMs: evento.status === 'running' ? undefined : (evento.ms ?? evento.duracao_ms),
    }));
}

// Funde a resposta de POST /post_execution_progress na lista de mensagens
// (T048/T050, specs/execution_progress/).
//
// Fora do componente porque a regra tem duas armadilhas que merecem teste
// proprio: nao duplicar mensagem que ja' esta na tela (o poll devolve
// PROGRESSO, nunca mensagem nova - quem nao esta na lista ainda nao foi
// carregado pelo historico, e inserir aqui criaria uma bolha fantasma), e
// nunca escrever `content` (enquanto a execucao roda o documento tem
// `results` vazio; nao existe texto parcial pra mostrar).
//
// Alem do estado, a fusao reidrata o INDICADOR de atividade
// (`activity_step`, `activity_started_at`, `activity_timeline`) a partir do
// progresso. Sem isso, uma execucao que continua rodando depois de o usuario
// sair e voltar para o chat so' aparecia enquanto um evento novo do SSE
// pintava a mensagem - e' o SSE que monta esses campos ao vivo, e a mensagem
// que volta do servidor nao os tem. Reidratados aqui, eles passam a ser
// funcao do estado do servidor: fechar o painel "Atividade" devolve o
// indicador inline em vez de deixar a mensagem vazia.
function aplicarProgressoDeExecucao(mensagens, mensagensDoProgresso, opcoes = {}) {
    const { rotuloDaTool = evento => evento.label || evento.name, agora = Date.now() } = opcoes;
    const porId = new Map((mensagensDoProgresso || []).map(m => [m.interaction_id, m]));
    if (porId.size === 0) return mensagens;

    return mensagens.map(msg => {
        const id = msg.interaction_id || msg._id;
        const progresso = id ? porId.get(id) : undefined;
        if (!progresso) return msg;

        const fundida = {
            ...msg,
            status: progresso.status,
            error: progresso.error || null,
            duracao_ms: progresso.duracao_ms,
            execution_events: progresso.execution_events || msg.execution_events || [],
        };

        // Mensagem com `clientId` nasceu nesta aba e ja' tem o SSE pintando o
        // indicador ao vivo, com mais informacao do que o poll carrega -
        // reidratar por cima so' rebaixaria o que ja' esta na tela.
        if (msg.clientId || progresso.status !== 'running') return fundida;

        return {
            ...fundida,
            activity_step: passoDeAtividade(progresso, rotuloDaTool),
            activity_started_at: inicioPelaDuracao(progresso.duracao_ms, agora),
            activity_timeline: timelineDeAtividade(progresso, agora, rotuloDaTool),
        };
    });
}

// Identidade de uma mensagem na tela - chave de render e alvo do painel
// "Atividade". `interaction_id` entra na cadeia porque mensagem vinda do
// historico so' tem esse campo (_build_assistant_message nao devolve `_id`):
// sem ele o painel se prendia ao indice da lista, que nao casa com nada na
// busca por clientId/_id e deixava a timeline reidratada inalcancavel.
function idDaMensagem(msg, index) {
    return msg.clientId || msg._id || msg.interaction_id || index;
}

// Ids que o poll estava observando e que sumiram da resposta: a execucao
// virou terminal entre duas batidas. E' o sinal de parada do contrato.
function idsQueFecharam(idsObservados, mensagensDoProgresso) {
    const vivos = new Set((mensagensDoProgresso || []).map(m => m.interaction_id));
    return idsObservados.filter(id => !vivos.has(id));
}

export {
    getFriendlyError,
    buildUploadAlert,
    aplicarProgressoDeExecucao,
    idDaMensagem,
    idsQueFecharam
};
